import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { MainWrapper, PageContainer, ThemeWrapper } from '@flixbus/honeycomb-react';
import { useTranslation } from 'react-i18next';
import '@flixbus/honeycomb-react/dist/css/honeycomb-helpers.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import './App.css';
import AppHeader from './components/header/AppHeader';
import Loader from './components/loader/Loader';
import CustomNotification from './components/notification/CustomNotification';
import AuthGuard from './components/auth/AuthGuard';
import ErrorBoundary from './components/errorBoundary/ErrorBoundary';
import PageTitle from './components/pageTitle/PageTitle';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { NotificationProvider, useNotification } from './contexts/NotificationContext';
import { registerLoadingSetter, registerErrorHandler } from './helpers/ApiCall';

// One chunk per page; a chunk that fails to load (old tab after a deploy) shows the ErrorBoundary page.
const PaxWidget = lazy(() => import('./pages/widget/PaxWidget'));
const Search = lazy(() => import('./pages/search/Search'));

function AppInner() {
    const { setLoading } = useLoading();

    useEffect(() => {
        registerLoadingSetter(setLoading);
    }, [setLoading]);

    const { t } = useTranslation();
    const { showNotification } = useNotification();

    useEffect(() => {
        registerErrorHandler(({ type }) => showNotification(t(`errors.${type}`), 'danger'));
        return () => registerErrorHandler(null);
    }, [showNotification, t]);

    return (
        <BrowserRouter>
            <ThemeWrapper>
                <MainWrapper full>
                    <Loader />
                    <ErrorBoundary>
                        <Suspense fallback={null}>
                            <Routes>
                                <Route
                                    path="/:rideId"
                                    element={
                                        <>
                                            <AuthGuard>
                                                <PageTitle title={t('titles.paxList')} />
                                                <PaxWidget />
                                            </AuthGuard>
                                        </>
                                    }
                                />
                                <Route
                                    path="/"
                                    element={
                                        <PageContainer Elem="main" hasFixedHeader>
                                            <AppHeader />
                                            <AuthGuard>
                                                <PageTitle title={t('titles.page.search')} />
                                                <Search />
                                            </AuthGuard>
                                        </PageContainer>
                                    }
                                />
                            </Routes>
                        </Suspense>
                    </ErrorBoundary>
                    <CustomNotification />
                </MainWrapper>
            </ThemeWrapper>
        </BrowserRouter>
    );
}

function App() {
    return (
        <LoadingProvider>
            <NotificationProvider>
                <AppInner />
            </NotificationProvider>
        </LoadingProvider>
    );
}

export default App;
